import http from './http';
import {PROJECT_BASE} from './paths';
import {withPerm} from './perm';

const BASE_PATH = PROJECT_BASE;

// 프로젝트 용어 목록 조회
export const getProjectTrmList = withPerm(async (pjtId) => {
  const response = await http.get(`${BASE_PATH}/${pjtId}/trm`);
  return response.data;
}, 'GET', `${BASE_PATH}/{pjtId}/trm`);

// 프로젝트 용어 단건 조회
export const getProjectTrm = withPerm(async (pjtId, pjtTrmId) => {
  const response = await http.get(`${BASE_PATH}/${pjtId}/trm/${pjtTrmId}`);
  return response.data;
}, 'GET', `${BASE_PATH}/{pjtId}/trm/{pjtTrmId}`);

// 프로젝트 용어 생성
export const createProjectTrm = withPerm(async (pjtId, payload) => {
  const response = await http.post(`${BASE_PATH}/${pjtId}/trm`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_PATH}/{pjtId}/trm`);

// 프로젝트 용어 수정
export const updateProjectTrm = withPerm(async (pjtId, pjtTrmId, payload) => {
  const response = await http.patch(`${BASE_PATH}/${pjtId}/trm/${pjtTrmId}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'PATCH', `${BASE_PATH}/{pjtId}/trm/{pjtTrmId}`); 

// 프로젝트 용어 삭제
export const deleteProjectTrm = withPerm(async (pjtId, pjtTrmId) => {
  const response = await http.delete(`${BASE_PATH}/${pjtId}/trm/${pjtTrmId}`);
  return response.data;
}, 'DELETE', `${BASE_PATH}/{pjtId}/trm/{pjtTrmId}`);

// 프로젝트 용어 일괄 생성 (엑셀 업로드)
export const createProjectTrmBatch = withPerm(async (pjtId, list = []) => {
  const response = await http.post(`${BASE_PATH}/${pjtId}/trm/batch`, list, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_PATH}/{pjtId}/trm/batch`);

// 프로젝트 용어 일괄 삭제
export const deleteProjectTrmBatch = withPerm(async (pjtId, idList = []) => {
  if (!Array.isArray(idList) || idList.length === 0) {
    throw new Error('idList must be a non-empty array');
  }
  const response = await http.delete(`${BASE_PATH}/${pjtId}/trm/batch`, {
    headers: { 'Content-Type': 'application/json' },
    data: idList,
  });
  return response.data;
}, 'DELETE', `${BASE_PATH}/{pjtId}/trm/batch`);

// 프로젝트 용어 업서트 배치 (서버 native upsert)
export const upsertProjectTrmBatch = withPerm(async (pjtId, list = []) => {
  const response = await http.post(`${BASE_PATH}/${pjtId}/trm/upsert-batch`, list, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}, 'POST', `${BASE_PATH}/{pjtId}/trm/upsert-batch`);

/**
 * 안전 업서트: 서버 upsert-batch 미구현(404/405/501) 시 폴백
 * - 동일 pjtTrmId 존재 시 updateProjectTrm (PUT)
 * - 없으면 createProjectTrmBatch 청크 삽입
 * @returns {Promise<{updated:number, created:number, errors:Object[], serverUsed:boolean}>}
 */
export async function safeUpsertProjectTrmBatch(pjtId, rows = [], options = {}) {
  const { forceClient = false } = options;
  if (!Array.isArray(rows)) throw new TypeError('rows must be an array');
  if (rows.length === 0) return { updated: 0, created: 0, errors: [], serverUsed: false };

  // 1) 서버 native upsert 먼저 시도
  if (!forceClient) {
    try {
      await upsertProjectTrmBatch(pjtId, rows);
      return { updated: rows.length, created: 0, errors: [], serverUsed: true };
    } catch (e) {
      const st = e?.response?.status;
      if (![404,405,501].includes(st)) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[safeUpsertProjectTrmBatch] native upsert failed:', st, e?.message);
        }
      }
      // 폴백 진행
    }
  }

  // 2) 기존 목록 조회 (응답 형태 normalize)
  let existingRaw;
  try { existingRaw = await getProjectTrmList(pjtId); } catch (e) { throw new Error('기존 용어 목록 조회 실패: ' + (e?.message || 'unknown')); }
  let existingList;
  if (Array.isArray(existingRaw)) existingList = existingRaw; else if (Array.isArray(existingRaw?.data)) existingList = existingRaw.data; else if (Array.isArray(existingRaw?.list)) existingList = existingRaw.list; else existingList = [];

  const existingMap = new Map();
  existingList.forEach(item => {
    const key = item?.pjtTrmId || item?.id || item?.uuid;
    if (key) existingMap.set(String(key), item);
  });

  const updates = [];
  const creates = [];
  rows.forEach(r => {
    const key = r.pjtTrmId || r.uuid || r.id;
    if (key && existingMap.has(String(key))) updates.push(r); else creates.push(r);
  });

  const errors = [];
  // 3) 업데이트 순차/소량 병렬 (동시성 5)
  const concurrency = 5;
  let idx = 0; let updatedCount = 0;
  async function runNext() {
    if (idx >= updates.length) return;
    const row = updates[idx++];
    const key = row.pjtTrmId || row.uuid || row.id;
    try { await updateProjectTrm(pjtId, key, row); updatedCount++; }
    catch (e) { errors.push({ type:'update', key, message: e?.message || 'update error', status: e?.response?.status }); }
    if (idx < updates.length) await runNext();
  }
  const starters = []; for (let i=0;i<Math.min(concurrency, updates.length);i++) starters.push(runNext());
  await Promise.all(starters);

  // 4) 생성 청크
  let createdCount = 0;
  if (creates.length) {
    const CHUNK = 500;
    for (let i=0;i<creates.length;i+=CHUNK) {
      const slice = creates.slice(i,i+CHUNK);
      try { await createProjectTrmBatch(pjtId, slice); createdCount += slice.length; }
      catch (e) { errors.push({ type:'createBatch', indexStart:i, size:slice.length, message:e?.message||'create batch error', status:e?.response?.status }); }
    }
  }
  return { updated: updatedCount, created: createdCount, errors, serverUsed: false };
}
